// Things to note/gotchas:
// The created_at left-hand property is converted to/from all_accounts_created_at (LeftConstants.ByClusterCreatedAt)
// to differentiate the by_cluster created-at conditional from the by_account created-at conditional
// The shared_properties conditional is stored in a separate useState hook from the rest of the `conditionals`,
// because it's handled very separately in the UI
import { Color, Spacing } from '@persona-id/design-tokens';
import { isEqual } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import { useGraphQuerying } from 'dashboard/components/GraphModule/hooks/useGraphQuerying';
import ByClusterQueryBody from 'dashboard/components/GraphQueryEditor/ByClusterQueryBody';
import {
  ClusterQueryData,
  defaultFormState,
  defaultSharedPropertiesConditional,
  FormState,
  formStateToQuery,
  isFormStateValid,
  isNotRunnable,
  NHopsQueryData,
  queryToFormState,
} from 'dashboard/components/GraphQueryEditor/form-state';
import NHopsQueryBody from 'dashboard/components/GraphQueryEditor/NHopsQueryBody';
import NodeLimit from 'dashboard/components/GraphQueryEditor/NodeLimit';
import TemplateLoadModal from 'dashboard/components/GraphQueryEditor/TemplateLoadModal';
import TemplateSaveModal from 'dashboard/components/GraphQueryEditor/TemplateSaveModal';
import { FormGraphQueryTemplateLoadData } from 'dashboard/forms/FormGraphQueryTemplateLoad';
import { FormGraphQueryTemplateSaveDataV2 } from 'dashboard/forms/FormGraphQueryTemplateSave/FormGraphQueryTemplateSaveV2';
import {
  GraphQueryBody,
  QueryConditionalConfig,
  QueryConfig,
  QueryType,
} from 'dashboard/models/graph-query';
import {
  GraphQueryTemplate,
  GraphQueryTemplateStatus,
} from 'dashboard/models/graph-query-template';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Sticker from 'design-system/components/Sticker';
import { Elevation } from 'design-system/variables/effect';
import { SegmentClient } from 'lib/clients/SegmentClient';
import { getNodeLimit } from 'lib/constants/graph';
import useToggle from 'lib/hooks/useToggle';

const StyledCard = styled(Card)`
  width: fit-content;
  min-width: 1024px;
  border-radius: ${BORDER_RADIUSES.rounded};
  box-shadow: ${Elevation.Mid};
`;

const StyledCardHeader = styled(Card.Title)`
  border-bottom: 1px solid ${Color.CoreSlate100};
  padding: ${Spacing[4]};
`;
const QueryBody = styled(Card.Body)`
  padding: 0 ${Spacing[4]} ${Spacing[4]} ${Spacing[4]};
`;

const NameTitle = styled.div<{ persisted: boolean }>`
  color: ${({ persisted }) => (persisted ? Color.PrimaryBlack : Color.CoreSlate700)};
  font-weight: 400;
  font-size: 16px;
  margin-right: ${Spacing[2]};
`;

const QueryActionContainer = styled(Card.Body)`
  border-top: 1px solid ${Color.CoreSlate100};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing[4]};
`;

const QueryActionButton = styled(Button)`
  width: auto;
`;

export interface GraphQueryEditorProps {
  accountConnectionConditionalConfig: QueryConfig;
  clearGraphQueryTemplate: () => void;
  graphQueryTemplate: GraphQueryTemplate | null;
  handleClearGraphQueryAndGraphQueryTemplate: () => void;
  handleLoadGraphQueryTemplate: (graphQueryTemplateId: string) => Promise<void>;
  handleQuery: (query: GraphQueryBody) => void;
  handleSaveGraphQueryTemplate: (
    id: string | null,
    name: string,
    description: string | null,
    payload: GraphQueryBody,
    saveAs: boolean,
  ) => Promise<boolean>;
  loadingGraphEnvironment: boolean;
  properties: string[];
  queryConfig: QueryConfig;
  sharedPropertiesConfig: QueryConditionalConfig;
  toggleShowGraphQueryEditor: () => void;
}

const GraphQueryEditor = ({
  accountConnectionConditionalConfig,
  clearGraphQueryTemplate,
  graphQueryTemplate,
  handleClearGraphQueryAndGraphQueryTemplate,
  handleLoadGraphQueryTemplate,
  handleQuery,
  handleSaveGraphQueryTemplate,
  loadingGraphEnvironment,
  properties,
  queryConfig,
  sharedPropertiesConfig,
  toggleShowGraphQueryEditor,
}: GraphQueryEditorProps) => {
  const [
    isTemplateSaveModalOpen,
    { enable: showTemplateSaveModal, disable: hideTemplateSaveModal },
  ] = useToggle(false);
  const [
    isTemplateLoadModalOpen,
    { enable: showTemplateLoadModal, disable: hideTemplateLoadModal },
  ] = useToggle(false);

  const handleTemplateSaveModalSubmit = (data: FormGraphQueryTemplateSaveDataV2) => {
    const success = handleSaveGraphQueryTemplate(
      data.id,
      data.name,
      data.description,
      makeQuery(),
      data.id == null,
    );
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (success) {
      hideTemplateSaveModal();
    }
  };
  const [showErrors, setShowErrors] = useState<boolean>(false);

  const [formState, setFormState] = useState<FormState>(defaultFormState(queryConfig));

  const graphQueryTemplateFormState = useMemo(() => {
    if (graphQueryTemplate != null && 'conditionals' in graphQueryTemplate.attributes.payload) {
      // Sometimes the payload won't include the type, so we'll handle it appropriately here.
      graphQueryTemplate.attributes.payload.type ||= graphQueryTemplate.attributes.type as
        | QueryType.ByAccount
        | QueryType.ByCluster
        | QueryType.NHopsFromAccount;
      if (
        graphQueryTemplate.attributes.payload.type !== QueryType.ByCluster &&
        graphQueryTemplate.attributes.payload.type !== QueryType.NHopsFromAccount
      ) {
        throw new Error('Expected a by-cluster or n-hops query');
      }
      return queryToFormState(graphQueryTemplate.attributes.payload);
    }
    return {};
  }, [graphQueryTemplate]);

  const isQueryModified = useMemo(() => {
    return !isEqual(graphQueryTemplateFormState, formState);
  }, [graphQueryTemplateFormState, formState]);

  // Note: Changes the query to be a by-cluster query if this function is invoked.
  const setClusterQueryData = (clusterQueryData: ClusterQueryData) => {
    setFormState({
      ...formState,
      queryData: clusterQueryData,
    });
  };
  // Note: Changes the query to be an n-hops query if this function is invoked.
  const setNHopsQueryData = (nHopsQueryData: NHopsQueryData) => {
    setFormState({
      ...formState,
      queryData: nHopsQueryData,
    });
  };

  const [nodeLimit, setNodeLimit] = useState<number>(getNodeLimit());

  const resetQueryEditor = () => {
    clearGraphQueryTemplate();
    setFormState(defaultFormState(queryConfig));
  };

  // initialize sharedPropertiesConditional once the config is ready
  useEffect(() => {
    if (sharedPropertiesConfig && !formState.queryData.sharedPropertiesConditional) {
      setFormState({
        ...formState,
        queryData: {
          ...formState.queryData,
          sharedPropertiesConditional: defaultSharedPropertiesConditional(sharedPropertiesConfig),
        },
      });
    }
  }, [formState, sharedPropertiesConfig]);

  const makeQuery = useCallback((): GraphQueryBody => {
    return formStateToQuery(
      formState,
      [], // knownNodeIds
      1000, // expandabilityLimit
      '', // startCursor - We use a fresh cursor whenever we construct a new query.
      sharedPropertiesConfig,
    );
  }, [formState, sharedPropertiesConfig]);

  const handleNodeLimitChange = useCallback(
    (limit: number) => {
      setNodeLimit(limit);
      setFormState({
        ...formState,
        nodeLimit: limit,
      });
    },
    [setNodeLimit, setFormState, formState],
  );

  const isValid = isFormStateValid(formState);

  const handleSubmit = useCallback(() => {
    if (!isValid) {
      setShowErrors(true);
    } else {
      setShowErrors(false);
      handleQuery(makeQuery());

      toggleShowGraphQueryEditor();
      SegmentClient.sendSegmentTrackEvent('Graph - Run Query');
    }
  }, [handleQuery, toggleShowGraphQueryEditor, makeQuery, setShowErrors, isValid]);

  const { currentGraphQuery } = useGraphQuerying();

  useEffect(() => {
    // If both graph query id and graph query template id existin in the initial render, there is an implicit understanding
    // that the GQT request finishes first. Therefore the graph query response will be prioritized on the "initial" render.
    // Clearly this is not the best solution, but something that works for now given the existing UI/UX.
    if (graphQueryTemplate != null && 'conditionals' in graphQueryTemplate.attributes.payload) {
      // Sometimes the payload won't include the type, so we'll handle it appropriately here.
      graphQueryTemplate.attributes.payload.type ||= graphQueryTemplate.attributes.type as
        | QueryType.ByAccount
        | QueryType.ByCluster
        | QueryType.NHopsFromAccount;
      if (
        graphQueryTemplate.attributes.payload.type !== QueryType.ByCluster &&
        graphQueryTemplate.attributes.payload.type !== QueryType.NHopsFromAccount
      ) {
        throw new Error('Expected a by-cluster or n-hops query');
      }
      setFormState(queryToFormState(graphQueryTemplate.attributes.payload));
    }
  }, [graphQueryTemplate]);

  useEffect(() => {
    // Update the conditionals to match the current GQT or GQ
    // We need to split out the shared_properties conditional when loading from the model,
    // to store it separately in the sharedPropertiesConditional state
    // Also, we use `all_accounts_created_at` to differentiate the By Cluster query vs the Created_At filter
    if (currentGraphQuery != null) {
      if (
        currentGraphQuery.attributes.params.type !== QueryType.ByCluster &&
        currentGraphQuery.attributes.params.type !== QueryType.NHopsFromAccount
      ) {
        throw new Error('Expected a by-cluster or n-hops query');
      }
      setFormState(queryToFormState(currentGraphQuery.attributes.params));
    }
  }, [currentGraphQuery]);

  const notRunnable = isNotRunnable(formState);
  const handleTemplateLoadModalSubmit = (data: FormGraphQueryTemplateLoadData) => {
    handleClearGraphQueryAndGraphQueryTemplate();
    const success = handleLoadGraphQueryTemplate(data.graphQueryTemplateId);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (success) {
      hideTemplateLoadModal();
    }
  };

  const graphQueryTemplateName = graphQueryTemplate?.attributes?.['name'];
  const graphQueryTemplateStatus = graphQueryTemplate?.attributes?.['status'];

  return (
    <>
      <StyledCard
        data-test='graph-explorer__query-editor-card'
        id='graph-explorer__query-editor-card'
      >
        <StyledCardHeader
          actions={
            <>
              <Button
                type='button'
                variant='basic'
                onClick={showTemplateLoadModal}
                id='graph-explorer__load-query-template-button'
              >
                Load
              </Button>
              <Button
                type='button'
                variant='basic'
                disabled={!isQueryModified && !!graphQueryTemplateName}
                onClick={() => {
                  if (!isValid) {
                    setShowErrors(true);
                  } else {
                    showTemplateSaveModal();
                  }
                }}
                id='graph-explorer__save-query-template-button'
              >
                {graphQueryTemplateName ? 'Save' : 'Save...'}
              </Button>
              <Button
                type='button'
                variant='basic'
                onClick={resetQueryEditor}
                id='graph-explorer__clear-query-template-button'
                disabled={!isQueryModified && !graphQueryTemplateName}
              >
                Clear
              </Button>
            </>
          }
        >
          <NameTitle persisted={!!graphQueryTemplateName}>
            {graphQueryTemplateName || 'Unsaved'}
          </NameTitle>
          {graphQueryTemplateName && isQueryModified && (
            <Sticker variant='yellow'>Modified</Sticker>
          )}
          {graphQueryTemplateStatus === GraphQueryTemplateStatus.Archived && (
            <Sticker variant='danger'>Archived</Sticker>
          )}
        </StyledCardHeader>
        <QueryBody>
          {formState.queryData.queryType === QueryType.ByCluster && (
            <ByClusterQueryBody
              clusterQueryData={formState.queryData}
              loadingGraphEnvironment={loadingGraphEnvironment}
              queryConfig={queryConfig}
              setClusterQueryData={setClusterQueryData}
              setNHopsQueryData={setNHopsQueryData}
              sharedPropertiesConfig={sharedPropertiesConfig}
              showErrors={showErrors}
            />
          )}
          {formState.queryData.queryType === QueryType.NHopsFromAccount && (
            <NHopsQueryBody
              accountConnectionConditionalConfig={accountConnectionConditionalConfig}
              loadingGraphEnvironment={loadingGraphEnvironment}
              nHopsQueryData={formState.queryData}
              properties={properties}
              queryConfig={queryConfig}
              setClusterQueryData={setClusterQueryData}
              setNHopsQueryData={setNHopsQueryData}
              sharedPropertiesConfig={sharedPropertiesConfig}
              showErrors={showErrors}
            />
          )}
        </QueryBody>
        <QueryActionContainer>
          <QueryActionButton
            type='submit'
            variant='primary'
            disabled={loadingGraphEnvironment || notRunnable}
            onClick={handleSubmit}
            id='graph-query-editor__submit'
          >
            Run query
          </QueryActionButton>
          <NodeLimit nodeLimit={nodeLimit} onChange={handleNodeLimitChange} />
        </QueryActionContainer>
      </StyledCard>
      <TemplateSaveModal
        graphQueryTemplate={graphQueryTemplate}
        onSubmit={(data) => {
          handleTemplateSaveModalSubmit(data);
        }}
        handleCloseModal={hideTemplateSaveModal}
        isTemplateSaveModalOpen={isTemplateSaveModalOpen}
      />
      <TemplateLoadModal
        isTemplateLoadModalOpen={isTemplateLoadModalOpen}
        handleCloseModal={hideTemplateLoadModal}
        onSubmit={handleTemplateLoadModalSubmit}
      />
    </>
  );
};

export default GraphQueryEditor;
